import './style.css'
import { ViteSSG } from 'vite-ssg'
import App from './App.vue'
import { routes, setupRouterGuards } from '@app/router/router'
import { createPinia } from 'pinia'
import { VueQueryPlugin, QueryClient } from '@tanstack/vue-query'
import { isApiError } from '@core/errors'
import { LanguageServiceKey } from '@core/injection-keys'
import { createLanguageService } from '@app/services/language.service'
import { i18n } from '@app/i18n'
import { PAPER_BACKGROUND_HEX } from '@app/utils/theme-colors'

export const createApp = ViteSSG(
  App,
  {
    routes,
    base: import.meta.env.BASE_URL,
  },
  async ({ app, router, initialState }) => {
    // === SSR-SAFE: runs on both server and client ===
    const pinia = createPinia()
    app.use(pinia)
    app.use(i18n)

    // Pinia state hydration
    if (import.meta.env.SSR) {
      initialState.pinia = pinia.state.value
    } else {
      pinia.state.value = initialState?.pinia || {}
    }

    // Vue Query (SSR-safe — no browser APIs)
    const queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          staleTime: 30000,
          retry: (failureCount, error) => {
            if (isApiError(error)) {
              if (!error.recoverable) return false
              return failureCount < 3
            }
            return failureCount < 1
          },
          retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
        },
        mutations: {
          retry: false,
        },
      },
    })
    app.use(VueQueryPlugin, { queryClient })

    // Language service (SSR-safe)
    app.provide(LanguageServiceKey, createLanguageService())

    // === CLIENT-ONLY: everything that needs a browser ===
    if (!import.meta.env.SSR) {
      // Boot timeout. INIT_APP_TIMEOUT_MS must stay under the index.html 8000ms
      // splash auto-remove so init resolves (into ready or the error UI) before
      // the splash hard-removes itself.
      const INIT_APP_TIMEOUT_MS = 7000

      // Reject (or resolve) a promise if it doesn't settle within `ms`.
      function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
        let timer: ReturnType<typeof setTimeout>
        return Promise.race([
          promise.finally(() => clearTimeout(timer)),
          new Promise<T>((_, reject) => {
            timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
          }),
        ])
      }

      try {
      // CRITICAL: Mock init must happen before any Telegram SDK usage
      const { initPlatformMock, injectMockInitData, isUserMockEnabled, getSelectedMockUser } = await import('@core/platform-mock')
      const { MOCK_USERS } = await import('@app/config/mockUsers')

      initPlatformMock(MOCK_USERS)
      // Inject mock initData for the login-as debug switch. Gated on login-as
      // (which user), NOT TG-platform mock — so you can authenticate as a mock
      // user while staying on the web platform.
      if (isUserMockEnabled()) {
        let mockUser = getSelectedMockUser()
        if (!mockUser?.user_id || !mockUser?.username) {
          mockUser = MOCK_USERS[0] ?? null
        }
        if (mockUser) {
          injectMockInitData(mockUser)
        }
      }

      // We deliberately do NOT load telegram.org/js/telegram-web-app.js here.
      // @twa-dev/sdk (imported by useTelegram.ts) already BUNDLES the Telegram Web
      // App SDK and initializes it at import time — including the incoming-event
      // bridge (window.Telegram.WebView / TelegramGameProxy_receiveEvent). Loading
      // the official script AFTER that overwrites the bridge, so the client's
      // `back_button_pressed` (and every other incoming event) routes to the
      // official instance while our BackButton.onClick handler lives on the
      // @twa-dev instance — the native back button then shows but never fires.
      // Keeping the bundled SDK as the sole instance = one consistent event bus.
      // (The script used to load synchronously in index.html BEFORE this bundle,
      // so @twa-dev won the overwrite race and back worked; moving it to a dynamic
      // post-bundle load flipped the order and silently broke the native back button.)

      // Now safe to use platform APIs
      const { usePlatform } = await import('@core/platform')
      const platform = usePlatform()

      // Telegram chrome colours = the page stock, so the header, the WebView
      // background (pre-paint + iOS overscroll) and the bottom bar never show the
      // user's Telegram theme colour. The two newer setters are version-guarded
      // in core (Bot API 6.1 / 7.10) and no-ops on web.
      platform.setHeaderColor(PAPER_BACKGROUND_HEX)
      platform.setBackgroundColor(PAPER_BACKGROUND_HEX)
      platform.setBottomBarColor(PAPER_BACKGROUND_HEX)
      platform.expand()

      // Platform CSS classes
      if (platform.telegramPlatform !== 'unknown') {
        switch (platform.telegramPlatform) {
          case 'android':
          case 'android_x':
          case 'web':
          case 'weba':
          case 'tdesktop':
            document.body.classList.add('is-material')
            break
          case 'ios':
          case 'macos':
            document.body.classList.add('is-apple')
            break
          default:
            document.body.classList.add(`is-${platform.telegramPlatform}`)
            break
        }
      }
      document.body.classList.add(`is-exact-${platform.telegramPlatform}`)

      // Sentry
      const { initSentry, Sentry } = await import('@/lib/sentry')
      initSentry(app)

      // PostHog error tracking
      const { usePostHog } = await import('@core/analytics')
      const { posthog } = usePostHog()

      // Error handlers
      app.config.errorHandler = (error, _instance, info) => {
        // Log + capture only. A render/lifecycle error in one component is
        // contained by Vue; navigating the whole app to /error is too aggressive
        // (and can loop if the error recurs on re-render).
        console.error('Vue error:', error)
        Sentry.captureException(error, { extra: { componentInfo: info } })
        posthog.capture('frontend_error', {
          message: error instanceof Error ? error.message : String(error),
          type: 'vue_error',
          component_info: info,
          route: window.location.pathname,
        })
      }

      window.addEventListener('unhandledrejection', (event) => {
        // Log + capture only — a stray rejected promise must not nuke the app to
        // the error screen. The app is almost always still usable.
        console.error('Unhandled promise rejection:', event.reason)
        Sentry.captureException(event.reason)
        posthog.capture('frontend_error', {
          message: String(event.reason),
          type: 'unhandled_rejection',
          route: window.location.pathname,
        })
      })

      window.addEventListener('error', (event) => {
        // Log + capture only — a single runtime/resource error shouldn't blow away
        // the whole app.
        console.error('Runtime error:', event.error)
        Sentry.captureException(event.error)
        posthog.capture('frontend_error', {
          message: event.message,
          filename: event.filename,
          line: event.lineno,
          type: 'runtime_error',
          route: window.location.pathname,
        })
      })

      router.onError((error) => {
        console.error('Router error:', error)
        Sentry.captureException(error)
        posthog.capture('frontend_error', {
          message: error instanceof Error ? error.message : String(error),
          type: 'router_error',
          route: window.location.pathname,
        })
        router.push('/error')
      })

      // API client config
      const { configureApiClient } = await import('@/config/kubb-config')
      configureApiClient()

      // Router guards (uses platform, query client — client-only)
      setupRouterGuards(router)

      // Initialize app (fetches user, PostHog identification)
      const { initApp, setInitError } = await import('@app/composables/useAppInit')
      try {
        await withTimeout(initApp(queryClient, router), INIT_APP_TIMEOUT_MS, 'initApp')
      } catch (err) {
        // On a *thrown* init error, initializeApp already set useAppInit().error
        // (App.vue renders <ErrorScreen> from it). But on a *timeout/hang* — the
        // case this timeout exists for — initializeApp never reaches its catch, so
        // the error state is still empty and App.vue would show the blank
        // LoadingScreen forever. setInitError() forces the error state so the retry
        // screen shows. Calling it on both paths is safe (same error object).
        // Do NOT rethrow: rethrowing would bubble to the global unhandledrejection
        // handler and (post-#7) just be captured, leaving the splash up.
        console.error('[boot] initApp failed/timed out:', err)
        Sentry.captureException(err)
        posthog.capture('frontend_error', { type: 'boot_init_failed', message: String(err) })
        setInitError(err)
      }

      // Eruda debug console
      if (import.meta.env.VITE_DEBUG === 'true') {
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/eruda'
        script.onload = () => {
          // @ts-expect-error - eruda is loaded dynamically
          window.eruda.init()
        }
        document.body.appendChild(script)
      }

      // Telegram platform ready
      requestAnimationFrame(() => {
        platform.disableVerticalSwipes()
        platform.ready()
      })
      } finally {
        // Always reveal the app, even if a boot step above threw, so the splash
        // is removed in both the happy and failure paths exactly once.
        //
        // Reveal the app once the router has resolved the initial navigation.
        // For Telegram launches the `beforeEach` guard has by now redirected
        // marketing routes → HOME_ROUTE, so removing the pre-paint splash here
        // reveals the user directly on the correct screen (no marketing flash).
        // (ViteSSG mounts after this setup callback; isReady resolves post-mount.)
        // index.html already installs an 8000ms hard failsafe, so no JS one here.
        void router.isReady().finally(() => {
          document.documentElement.classList.remove('tma-launch')
          // Web deep-load/refresh onto an app route hides #app behind 'app-launch'
          // (index.html) to mask the landing-fallback flash; reveal once the router
          // has resolved the real route.
          document.documentElement.classList.remove('app-launch')
        })
      }
    }
  },
  {
    // Hydrate the pre-rendered (SSG) routes on the client instead of doing a
    // full client re-render. Without this, vite-ssg uses createApp() (not
    // createSSRApp), so app.mount() overwrites the pre-rendered DOM — which
    // discards the SSG markup and replays every entrance animation on load.
    hydration: true,
  },
)
